/**
 * Die-cut sticker geometry: the white keyline a cutter follows around a
 * shopper's artwork.
 *
 * The whole feature is one raster pipeline with five named passes, and every
 * one of them is deterministic — no browser, no provider, no floating-point
 * surprise. The same pixels in give the same cut path out, which is why the
 * editor's preview, the checkout check and the production file can all trust
 * one another.
 *
 *   1. **Ink.** Any pixel the renderer painted at all (alpha > 0) is artwork.
 *   2. **Border.** The ink silhouette is grown by the shopper's `borderMm`,
 *      through an exact Euclidean distance transform, into the white margin
 *      the sticker is known by.
 *   2½. **Snug** (outline `'snug'` only). The gaps between pieces are closed
 *      just far enough to join them — the longest link the spanning tree
 *      needs, halved, capped at `maxLinkMm` — so a word becomes one tight
 *      shape with a thin rim instead of letters in a thick even halo.
 *   3. **Bridges.** Artwork that falls into separate islands (two words, a
 *      letter and its dot) is joined by the shortest possible links — a
 *      minimum spanning tree over the islands — each stamped into the mask as
 *      a capsule `bridgeMm` wide. One sticker comes off the sheet, not five.
 *   4. **Holes.** Anything enclosed (the counter of an "o", a gap between
 *      arms) is filled: a cutter cannot lift an island out of the middle.
 *   5. **Outline.** The mask's boundary is walked as one closed ring of
 *      directed pixel edges and reduced to its corners.
 *
 * Millimetres never appear here: the caller converts, because it is the caller
 * that knows the raster's resolution. `STICKER_LIMITS` is the single source of
 * every bound, including the largest sticker the contour can be traced for.
 */

export interface StickerSettings {
  /** White margin around the artwork, millimetres. */
  borderMm: number;
  /** false: photos keep their own edge. Omitted preserves legacy order geometry. */
  imageBorder?: boolean;
  /** Width of the links that join separate islands, millimetres. */
  bridgeMm: number;
  /**
   * How the white backing follows the artwork.
   *
   * `'even'` (or omitted, which is how every order before it was stored) grows
   * the ink by `borderMm` everywhere and bridges whatever is still apart.
   * `'snug'` keeps that `borderMm` rim but also closes the gaps BETWEEN the
   * pieces — the space between letters, a word and the next line — only as far
   * as it takes to join them (never more than `STICKER_LIMITS.maxLinkMm`), so a
   * name comes out as one tight white shape instead of a thick even halo.
   */
  outline?: StickerOutline;
}

export type StickerOutline = 'even' | 'snug';
export const STICKER_OUTLINES: readonly StickerOutline[] = ['even', 'snug'];

export const STICKER_DEFAULTS: StickerSettings = { borderMm: 1.5, bridgeMm: 2, imageBorder: false, outline: 'snug' };

/**
 * Every bound the sticker pipeline enforces, in one place so prose numbers
 * cannot drift apart from the code (`DESIGNER_CAPABILITIES.sticker` and
 * `configureSticker` both read from here).
 *
 * `maxEdge` is the largest contour raster we will trace, and `minPxPerMm` the
 * coarsest resolution a production contour may be traced at — together they
 * fix the largest sticker we can manufacture (`stickerMaxSideMm()`).
 */
export const STICKER_LIMITS = {
  minBorderMm: 0.5,
  maxBorderMm: 10,
  minBridgeMm: 1,
  maxBridgeMm: 10,
  /** Longest raster side, pixels. */
  maxEdge: 2048,
  /** Production contours are traced at 5–10 px/mm. */
  minPxPerMm: 5,
  maxPxPerMm: 10,
  /** The editor may trace this coarsely to keep a drag interactive. */
  previewMinPxPerMm: 2,
  /** Separate islands a cutter is asked to bridge before we refuse. */
  maxBridgedIslands: 24,
  /** Connected components the tracer will consider at all. */
  maxIslands: 512,
  /**
   * The widest gap a snug outline closes on its own, as a radius: pieces up to
   * twice this far apart are joined by the backing itself, anything farther by
   * a bridge. Keeps two distant words from turning into one big white slab.
   */
  maxLinkMm: 6,
} as const;

/**
 * The longest side a sticker may have, millimetres.
 *
 * A contour must be traced at `minPxPerMm` or finer to be a cut path rather
 * than a staircase, and the raster may not exceed `maxEdge` — so the physical
 * limit falls straight out of the two, and no other file may restate it.
 */
export function stickerMaxSideMm(): number {
  return STICKER_LIMITS.maxEdge / STICKER_LIMITS.minPxPerMm;
}

export function validStickerSettings(value: unknown): value is StickerSettings {
  if (!value || typeof value !== 'object') return false;
  const settings = value as StickerSettings;
  const border = Number.isFinite(settings.borderMm)
    && settings.borderMm >= STICKER_LIMITS.minBorderMm
    && settings.borderMm <= STICKER_LIMITS.maxBorderMm;
  const bridge = Number.isFinite(settings.bridgeMm)
    && settings.bridgeMm >= STICKER_LIMITS.minBridgeMm
    && settings.bridgeMm <= STICKER_LIMITS.maxBridgeMm;
  return border && bridge && (settings.imageBorder === undefined || typeof settings.imageBorder === 'boolean')
    && (settings.outline === undefined || STICKER_OUTLINES.includes(settings.outline));
}

/** The snug outline's link radius in millimetres; 0 for an even border. */
export function stickerLinkMm(settings: StickerSettings): number {
  return settings.outline === 'snug' ? STICKER_LIMITS.maxLinkMm : 0;
}

export interface StickerContour {
  /** One closed outer contour, in raster coordinates. Last point equals first. */
  points: Array<[number, number]>;
  mask: Uint8Array;
  width: number;
  height: number;
  joinedIslands: number;
  bridges: Array<{ from: [number, number]; to: [number, number] }>;
  touchesEdge: boolean;
}

// ── Pass 2: grow the silhouette ──────────────────────────────────────────────

/**
 * Dilation by an exact Euclidean radius, in linear time.
 *
 * Felzenszwalb & Huttenlocher's distance transform: the squared distance to
 * the nearest set pixel is the lower envelope of one parabola per pixel, and
 * that envelope is separable — a pass down every column, then a pass across
 * every row. A morphological dilation by a disc is then just "distance ≤ r",
 * which is what makes a 10 mm border as cheap as a 0.5 mm one.
 */
function dilate(mask: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  const longest = Math.max(width, height);
  // `source` is the pass input, `distance` its output; `hull` and `boundary`
  // are the parabolas of the lower envelope and where they take over.
  const source = new Float64Array(longest);
  const distance = new Float64Array(longest);
  const hull = new Int32Array(longest);
  const boundary = new Float64Array(longest + 1);
  const columns = new Float32Array(width * height);

  const envelope = (length: number): void => {
    let top = 0;
    hull[0] = 0;
    boundary[0] = -Infinity;
    boundary[1] = Infinity;
    for (let q = 1; q < length; q++) {
      const intersect = (): number =>
        ((source[q]! + q * q) - (source[hull[top]!]! + hull[top]! * hull[top]!)) / (2 * (q - hull[top]!));
      let crossing = intersect();
      while (crossing <= boundary[top]!) { top--; crossing = intersect(); }
      hull[++top] = q;
      boundary[top] = crossing;
      boundary[top + 1] = Infinity;
    }
    top = 0;
    for (let q = 0; q < length; q++) {
      while (boundary[top + 1]! < q) top++;
      distance[q] = (q - hull[top]!) ** 2 + source[hull[top]!]!;
    }
  };

  // A set pixel is at distance 0; everything else starts effectively infinite.
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) source[y] = mask[y * width + x] ? 0 : 1e12;
    envelope(height);
    for (let y = 0; y < height; y++) columns[y * width + x] = distance[y]!;
  }
  const grown = new Uint8Array(mask.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) source[x] = columns[y * width + x]!;
    envelope(width);
    for (let x = 0; x < width; x++) grown[y * width + x] = distance[x]! <= radius * radius ? 1 : 0;
  }
  return grown;
}

/**
 * The four orthogonal neighbours of a pixel.
 *
 * Four-connectivity on purpose: with eight, two shapes that merely touch at a
 * corner would count as joined, and the cutter would try to follow a path one
 * point wide. Four says "these are separate, bridge them".
 */
function neighbours(index: number, width: number, count: number, visit: (neighbour: number) => void): void {
  if (index % width) visit(index - 1);
  if (index % width < width - 1) visit(index + 1);
  if (index >= width) visit(index - width);
  if (index + width < count) visit(index + width);
}

function assertTraceable(rgba: Uint8Array, width: number, height: number, borderPx: number, bridgePx: number): void {
  const rasterOk = Number.isInteger(width) && Number.isInteger(height)
    && width >= 3 && height >= 3
    && width <= STICKER_LIMITS.maxEdge && height <= STICKER_LIMITS.maxEdge
    && rgba.length === width * height * 4;
  const borderOk = Number.isFinite(borderPx) && borderPx >= 1 && borderPx <= 512;
  const bridgeOk = Number.isFinite(bridgePx) && bridgePx >= 2 && bridgePx <= 512;
  if (!rasterOk || !borderOk || !bridgeOk) throw new Error('Invalid sticker raster or physical outline size');
}

/**
 * Trace the alpha actually printed, expand its silhouette, connect components
 * using a minimum spanning tree of wavefront neighbours, then fill holes.
 * Uses four-connectivity so touching corners cannot masquerade as cuttable joins.
 */
export function stickerContour(rgba: Uint8Array, width: number, height: number, borderPx: number, bridgePx: number, unborderedRgba?: Uint8Array, linkPx = 0): StickerContour {
  assertTraceable(rgba, width, height, borderPx, bridgePx);
  if (!Number.isFinite(linkPx) || linkPx < 0 || linkPx > 512) throw new Error('Invalid sticker link radius');
  const count = width * height;

  // ── Pass 1: ink ───────────────────────────────────────────────────────────
  // Any paint at all is artwork. A near-transparent anti-aliased edge still
  // belongs to the shape; the border that follows is far wider than it.
  const ink = new Uint8Array(count);
  for (let i = 0; i < count; i++) ink[i] = rgba[i * 4 + 3]! > 0 ? 1 : 0;

  // ── Pass 2: border ────────────────────────────────────────────────────────
  if (unborderedRgba && unborderedRgba.length !== rgba.length) throw new Error('Invalid sticker photo raster');
  const borderInk = unborderedRgba ? new Uint8Array(count) : ink;
  if (unborderedRgba) for (let i = 0; i < count; i++) borderInk[i] = ink[i] && !unborderedRgba[i * 4 + 3] ? 1 : 0;
  const mask = dilate(borderInk, width, height, borderPx);
  // Photo footprints already form a cuttable piece. Only ink outside those
  // footprints needs an expanded border; text within a photo cannot add a halo.
  // Unexpanded photos remain part of the same union and receive bridges.
  if (unborderedRgba) for (let i = 0; i < count; i++) if (ink[i]) mask[i] = 1;

  // `queue` is the shared BFS ring (every pixel enters at most once); `owner`
  // labels islands; `seed` remembers, for a pixel reached by a wavefront, which
  // mask pixel its front started from.
  const owner = new Int32Array(count);
  const queue = new Int32Array(count);
  const seed = new Int32Array(count);

  // ── Pass 2½: snug outline ─────────────────────────────────────────────────
  // Close the gaps between the pieces just wide enough to join them: find the
  // longest link the spanning tree below would need, and close the mask
  // (dilate, then erode) by half of it. Closing only ever ADDS backing — it
  // fills the space between letters, never eats into the `borderPx` rim — and
  // what it could not join (a dot touching nothing along its side) still gets
  // an ordinary bridge in pass 3.
  if (linkPx > 0) {
    const pieces = labelIslands(mask, width, count, owner, queue);
    if (pieces > 1 && pieces <= STICKER_LIMITS.maxIslands) {
      const links = spanningLinks(nearestPairs(owner, width, count, queue, seed), pieces);
      const longest = Math.sqrt(Math.max(...links.map(link => link.distance)));
      // Half the gap from each side, plus a pixel so the two fronts overlap —
      // and a little more while that still leaves pieces apart: where two
      // shapes meet only at a point (a letter's foot over a digit's top) the
      // erosion reopens a neck the dilation just made. The smallest radius
      // that joins everything wins; the cap stops it becoming a slab.
      let radius = Math.min(linkPx, longest / 2 + 1);
      for (let attempt = 0; ; attempt++) {
        const trial = mask.slice();
        closeMask(trial, width, height, radius);
        owner.fill(0);
        const left = labelIslands(trial, width, count, owner, queue);
        if (left === 1 || radius >= linkPx || attempt === 5) { mask.set(trial); break; }
        radius = Math.min(linkPx, radius * 1.35 + 1);
      }
    }
    owner.fill(0);
  }

  // ── Pass 3: islands and bridges ───────────────────────────────────────────
  const islands = labelIslands(mask, width, count, owner, queue);
  if (!islands) throw new Error('Sticker has no visible artwork');
  if (islands > STICKER_LIMITS.maxIslands) {
    throw new Error(`Sticker artwork has more than ${STICKER_LIMITS.maxIslands} separate islands; simplify the artwork`);
  }

  const bridges: StickerContour['bridges'] = [];
  if (islands > 1) {
    for (const link of spanningLinks(nearestPairs(owner, width, count, queue, seed), islands)) {
      const ax = link.from % width + 0.5, ay = Math.floor(link.from / width) + 0.5;
      const bx = link.to % width + 0.5, by = Math.floor(link.to / width) + 0.5;
      bridges.push({ from: [ax, ay], to: [bx, by] });
      stampBridge(mask, width, height, ax, ay, bx, by, bridgePx);
    }
  }

  // ── Pass 4: holes ─────────────────────────────────────────────────────────
  // Flood the transparent space inward from the raster's border. Whatever the
  // flood never reaches is enclosed by the sticker, so it becomes sticker.
  owner.fill(0);
  let head = 0, tail = 0;
  const enterExterior = (index: number): void => {
    if (!mask[index] && !owner[index]) { owner[index] = 1; queue[tail++] = index; }
  };
  for (let x = 0; x < width; x++) { enterExterior(x); enterExterior((height - 1) * width + x); }
  for (let y = 0; y < height; y++) { enterExterior(y * width); enterExterior(y * width + width - 1); }
  while (head < tail) neighbours(queue[head++]!, width, count, enterExterior);
  for (let i = 0; i < count; i++) if (!mask[i] && !owner[i]) mask[i] = 1;

  // ── Pass 5: outline ───────────────────────────────────────────────────────
  const { points, touchesEdge } = walkOutline(mask, width, height, count);
  return { points, mask, width, height, joinedIslands: islands - 1, bridges, touchesEdge };
}

/** Labels each 4-connected run of the mask 1..n in `owner`; returns n. */
function labelIslands(mask: Uint8Array, width: number, count: number, owner: Int32Array, queue: Int32Array): number {
  let islands = 0;
  for (let i = 0; i < count; i++) {
    if (!mask[i] || owner[i]) continue;
    islands++;
    let head = 0, tail = 1;
    queue[0] = i;
    owner[i] = islands;
    while (head < tail) {
      const pixel = queue[head++]!;
      neighbours(pixel, width, count, neighbour => {
        if (mask[neighbour] && !owner[neighbour]) { owner[neighbour] = islands; queue[tail++] = neighbour; }
      });
    }
  }
  return islands;
}

interface IslandLink { a: number; b: number; from: number; to: number; distance: number }

/** Reuse only the deterministic island connection geometry for flat backing.
 * Does not change sticker borders, hole filling, defaults or existing callers.
 */
export function connectRasterIslands(mask:Uint8Array,width:number,height:number,bridgePx:number,maxGapPx:number) {
  if(mask.length!==width*height||bridgePx<2||!Number.isFinite(bridgePx)||maxGapPx<=0||!Number.isFinite(maxGapPx))throw new Error('Invalid flat bridge settings');
  const owner=new Int32Array(mask.length),queue=new Int32Array(mask.length),seed=new Int32Array(mask.length);
  const islands=labelIslands(mask,width,mask.length,owner,queue);
  if(islands>STICKER_LIMITS.maxIslands)throw new Error('Too many flat artwork pieces');
  const links=islands>1?spanningLinks(nearestPairs(owner,width,mask.length,queue,seed),islands):[];
  for(const link of links)if(Math.sqrt(link.distance)>maxGapPx)throw new Error('Flat artwork needs a bridge longer than the selected maximum gap');
  for(const link of links)stampBridge(mask,width,height,link.from%width+.5,Math.floor(link.from/width)+.5,link.to%width+.5,Math.floor(link.to/width)+.5,bridgePx);
  return {joinedIslands:links.length,maxGapPx:links.length?Math.sqrt(Math.max(...links.map(l=>l.distance))):0};
}

/**
 * The closest pixel pair between every two neighbouring islands.
 *
 * Grow every island outward one step at a time (a multi-source BFS over the
 * empty space). Where two fronts meet, the mask pixels they started from are
 * the closest pair of those two islands along that meeting line — the
 * cheapest place to put a link. Keeps the shortest candidate per pair.
 * Overwrites `owner` for the empty pixels it floods.
 */
function nearestPairs(owner: Int32Array, width: number, count: number, queue: Int32Array, seed: Int32Array): IslandLink[] {
  let head = 0, tail = 0;
  for (let i = 0; i < count; i++) if (owner[i]) { seed[i] = i; queue[tail++] = i; }
  const candidates = new Map<string, IslandLink>();
  while (head < tail) {
    const pixel = queue[head++]!;
    neighbours(pixel, width, count, neighbour => {
      if (!owner[neighbour]) {
        owner[neighbour] = owner[pixel]!;
        seed[neighbour] = seed[pixel]!;
        queue[tail++] = neighbour;
        return;
      }
      if (owner[neighbour] === owner[pixel]) return;
      const a = Math.min(owner[pixel]!, owner[neighbour]!);
      const b = Math.max(owner[pixel]!, owner[neighbour]!);
      const from = seed[pixel]!, to = seed[neighbour]!;
      const distance = (from % width - to % width) ** 2 + (Math.floor(from / width) - Math.floor(to / width)) ** 2;
      const key = `${a}:${b}`;
      const best = candidates.get(key);
      if (!best || distance < best.distance) candidates.set(key, { a, b, from, to, distance });
    });
  }
  return [...candidates.values()];
}

/**
 * Kruskal over the candidates: shortest first, skipping any link whose two
 * islands are already connected, so N islands cost exactly N-1 links.
 */
function spanningLinks(candidates: IslandLink[], islands: number): IslandLink[] {
  const parent = Array.from({ length: islands + 1 }, (_, i) => i);
  const root = (node: number): number => {
    while (parent[node] !== node) { parent[node] = parent[parent[node]!]!; node = parent[node]!; }
    return node;
  };
  const chosen: IslandLink[] = [];
  for (const candidate of candidates.sort((x, y) => x.distance - y.distance || x.a - y.a || x.b - y.b)) {
    const a = root(candidate.a), b = root(candidate.b);
    if (a === b) continue;
    parent[a] = b;
    chosen.push(candidate);
  }
  return chosen;
}

/**
 * Morphological closing by a disc, in place: dilate, then erode by the same
 * radius. Worked on a copy padded by the radius, so the raster's own edge
 * reads as empty space — otherwise backing that reached the edge could never
 * be eroded back from it, and a snug outline would report a clipped border
 * the artwork does not have.
 */
function closeMask(mask: Uint8Array, width: number, height: number, radius: number): void {
  const pad = Math.ceil(radius) + 1;
  const pw = width + pad * 2, ph = height + pad * 2;
  const padded = new Uint8Array(pw * ph);
  for (let y = 0; y < height; y++) padded.set(mask.subarray(y * width, (y + 1) * width), (y + pad) * pw + pad);
  const grown = dilate(padded, pw, ph, radius);
  for (let i = 0; i < grown.length; i++) grown[i] = grown[i] ? 0 : 1;
  const outside = dilate(grown, pw, ph, radius);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) if (!outside[(y + pad) * pw + x + pad]) mask[y * width + x] = 1;
  }
}

/** Paints one bridge into the mask: a capsule (a thick line with round caps). */
function stampBridge(
  mask: Uint8Array,
  width: number,
  height: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  bridgePx: number,
): void {
  // A floor of 1.5 px keeps the very thinnest bridge from vanishing into the
  // raster grid, where the outline walk could not close around it.
  const radius = Math.max(1.5, bridgePx / 2);
  const dx = bx - ax, dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  const top = Math.max(0, Math.floor(Math.min(ay, by) - radius));
  const bottom = Math.min(height, Math.ceil(Math.max(ay, by) + radius));
  const left = Math.max(0, Math.floor(Math.min(ax, bx) - radius));
  const right = Math.min(width, Math.ceil(Math.max(ax, bx) + radius));
  for (let y = top; y < bottom; y++) {
    for (let x = left; x < right; x++) {
      // Distance from the pixel centre to the segment: project, clamp, measure.
      const along = Math.max(0, Math.min(1, ((x + 0.5 - ax) * dx + (y + 0.5 - ay) * dy) / (lengthSquared || 1)));
      const offX = x + 0.5 - ax - along * dx, offY = y + 0.5 - ay - along * dy;
      if (offX * offX + offY * offY <= radius * radius) mask[y * width + x] = 1;
    }
  }
}

/**
 * The mask's boundary as one closed ring of corner points.
 *
 * Every mask pixel contributes the edges of its own square that face empty
 * space, directed so the sticker is always on the right. Those edges join head
 * to tail into exactly one loop (holes were filled in pass 4), so walking from
 * any edge and consuming each exactly once must return to the start having used
 * them all — if it does not, the mask was not a single simple form and we say
 * so rather than hand a cutter a path that crosses itself.
 *
 * Coordinates are LATTICE points (pixel corners), hence the `width + 1` stride.
 */
function walkOutline(
  mask: Uint8Array,
  width: number,
  height: number,
  count: number,
): { points: Array<[number, number]>; touchesEdge: boolean } {
  const stride = width + 1;
  const outgoing = new Map<number, number[]>();
  const add = (from: number, to: number): void => {
    const list = outgoing.get(from) ?? [];
    list.push(to);
    outgoing.set(from, list);
  };
  let touchesEdge = false;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!mask[y * width + x]) continue;
      const corner = y * stride + x;
      // Mask on the raster's own border means the white margin is clipped: the
      // sticker would be cut flush with the sheet instead of having a rim.
      if (!x || !y || x === width - 1 || y === height - 1) touchesEdge = true;
      if (!y || !mask[(y - 1) * width + x]) add(corner, corner + 1);
      if (x === width - 1 || !mask[y * width + x + 1]) add(corner + 1, corner + 1 + stride);
      if (y === height - 1 || !mask[(y + 1) * width + x]) add(corner + 1 + stride, corner + stride);
      if (!x || !mask[y * width + x - 1]) add(corner + stride, corner);
    }
  }

  const start = outgoing.keys().next().value as number;
  const points: Array<[number, number]> = [];
  let current = start, steps = 0;
  do {
    points.push([current % stride, Math.floor(current / stride)]);
    const next = outgoing.get(current)?.shift();
    if (next === undefined || ++steps > count * 4) throw new Error('Sticker outline could not be closed');
    current = next;
  } while (current !== start);
  if ([...outgoing.values()].some(list => list.length)) throw new Error('Sticker outline is not one simple form');

  // Drop vertices that lie on the straight line between their neighbours. The
  // test is an exact integer cross product, so a bridge two pixels wide keeps
  // both of its corners instead of being smoothed away.
  const corners = points.filter((point, index) => {
    const before = points[(index + points.length - 1) % points.length]!;
    const after = points[(index + 1) % points.length]!;
    return (point[0] - before[0]) * (after[1] - point[1]) !== (point[1] - before[1]) * (after[0] - point[0]);
  });
  corners.push(corners[0]!);
  return { points: corners, touchesEdge };
}

// ── The editor's seam ────────────────────────────────────────────────────────

/**
 * How densely the editor should raster a zone for a live contour.
 *
 * The designer owns a renderer that hands back pixels, not a `ResourceSource`,
 * so it cannot use `@pz/scene-render`'s `stickerContourPreview`. It renders
 * `zoneScene(config, answer, stickerPreviewPxPerMm(sizeMm))` itself and brings
 * the pixels here instead — which keeps the millimetre arithmetic in one file
 * rather than in a canvas module.
 */
export function stickerPreviewPxPerMm(sizeMm: { w: number; h: number }, cap = STICKER_LIMITS.previewMinPxPerMm + 1): number {
  const longest = Math.max(sizeMm.w, sizeMm.h);
  const affordable = STICKER_LIMITS.maxEdge / longest;
  const wanted = Math.max(STICKER_LIMITS.previewMinPxPerMm, Math.min(cap, STICKER_LIMITS.maxPxPerMm));
  return Math.min(wanted, affordable);
}

/**
 * The contour of a raster the caller already produced. Pure — no renderer.
 *
 * The one thing it adds over `stickerContour` is the millimetre conversion:
 * border and bridge are physical, and getting that multiplication wrong in a
 * UI file is exactly the kind of drift this module exists to prevent.
 */
export function stickerContourFromRaster(
  rgba: Uint8Array,
  width: number,
  height: number,
  settings: StickerSettings,
  pxPerMm: number,
  unborderedRgba?: Uint8Array,
): StickerContour {
  if (!validStickerSettings(settings)) throw new Error('Invalid sticker settings');
  return stickerContour(rgba, width, height, settings.borderMm * pxPerMm, settings.bridgeMm * pxPerMm, unborderedRgba, stickerLinkMm(settings) * pxPerMm);
}

/**
 * A traced contour reduced to what `designAreaProblems`' `stickerContour` hook
 * wants: is the white border clipped, and how many pieces had to be bridged.
 */
export function stickerVerdict(contour: StickerContour): { touchesEdge: boolean; islands: number } {
  return { touchesEdge: contour.touchesEdge, islands: contour.joinedIslands };
}

/** The contour as an SVG path, optionally rescaled (raster px → millimetres). */
export function contourSvgPath(points: Array<[number, number]>, scale = 1): string {
  return points.map(([x, y], i) => `${i ? 'L' : 'M'}${+(x * scale).toFixed(4)} ${+(y * scale).toFixed(4)}`).join('') + 'Z';
}
